import type { Locale } from '@/context/LangContext'
import { getLocale, localizedPath, siteUrl } from '@/lib/seo'
import { seo } from '@/data/seo'
import { vpsPlans, gpuPlans, sentryPlans, streamingPlans, type Plan } from '@/data/plans'
import { projectTypes } from '@/data/projectTypes'
import { quickApps } from '@/data/quickApps'
import { databases } from '@/data/databases'
import { webmcpServices, webmcpDevOpsTools, webmcpFaqs, type WebMcpServiceKey } from '@/data/webmcp'
import { CONSOLE_URL, consoleLoginUrl } from '@/lib/urls'

export type PricingService = 'vps' | 'gpu' | 'sentry' | 'streaming'
export type PlanRegion = 'iran' | 'europe' | 'us-asia'

export interface ServiceInfo {
  key: string
  path: string
  url: string
  title: string
  description: string
}

export interface FaqResult {
  topic: string
  question: string
  answer: string
}

function normalize(value: string): string {
  return value.trim().toLowerCase()
}

export function listServices(lang: string): ServiceInfo[] {
  const l: Locale = getLocale(lang)
  return webmcpServices.map((s) => {
    const entry = seo[s.path]
    return {
      key: s.key,
      path: s.path,
      url: `${siteUrl}${localizedPath(l, s.path === 'about' && false ? '' : s.path)}`,
      title: entry ? entry.title[l] : s.key,
      description: entry ? entry.description[l] : '',
    }
  })
}

function plansFor(service: PricingService): Plan[] {
  if (service === 'gpu') return gpuPlans
  if (service === 'sentry') return sentryPlans
  if (service === 'streaming') return streamingPlans
  return vpsPlans
}

export function getPricing(service: PricingService, region?: PlanRegion): Plan[] {
  const all = plansFor(service)
  if (!region || service !== 'vps') return all
  return all.filter((p) => p.category === region)
}

export interface RecommendInput {
  service?: PricingService
  region?: PlanRegion
  minRamGb?: number
  maxResults?: number
}

function ramGb(plan: Plan): number {
  const m = plan.ram.match(/(\d+)/)
  return m ? parseInt(m[1], 10) : 0
}

export function recommendPlan(input: RecommendInput): Plan[] {
  const service: PricingService = input.service ?? 'vps'
  const candidates = getPricing(service, input.region).filter((p) =>
    input.minRamGb ? ramGb(p) >= input.minRamGb : true,
  )
  const limit = input.maxResults && input.maxResults > 0 ? Math.min(input.maxResults, 5) : 3
  return candidates.slice(0, limit)
}

export function listProjectTypes(query?: string): { name: string; slug: string }[] {
  const q = query ? normalize(query) : ''
  if (!q) return projectTypes
  return projectTypes.filter(
    (p) => normalize(p.name).includes(q) || normalize(p.slug).includes(q),
  )
}

export function listQuickApps(query?: string): { name: string; slug: string }[] {
  const q = query ? normalize(query) : ''
  if (!q) return quickApps
  return quickApps.filter((a) => normalize(a.name).includes(q) || normalize(a.slug).includes(q))
}

export function listDatabases(query?: string): { name: string; slug: string }[] {
  const q = query ? normalize(query) : ''
  if (!q) return databases
  return databases.filter((d) => normalize(d.name).includes(q) || normalize(d.slug).includes(q))
}

export function listDevOpsTools(query?: string): string[] {
  const q = query ? normalize(query) : ''
  if (!q) return webmcpDevOpsTools
  return webmcpDevOpsTools.filter((t) => normalize(t).includes(q))
}

export function searchFaq(query: string, topic?: string, lang?: string): FaqResult[] {
  const l: Locale = getLocale(lang ?? 'en')
  const q = normalize(query)
  return webmcpFaqs
    .filter((f) => (topic ? f.topic === topic : true))
    .filter((f) => {
      if (!q) return true
      return (
        normalize(f.q.en).includes(q) ||
        f.q.fa.includes(query.trim()) ||
        f.q.ar.includes(query.trim()) ||
        normalize(f.a.en).includes(q)
      )
    })
    .slice(0, 5)
    .map((f) => ({ topic: f.topic, question: f.q[l], answer: f.a[l] }))
}

export function resolvePage(key: string): WebMcpServiceKey | null {
  const found = webmcpServices.find((s) => s.key === key || s.path === key)
  return found ? found.key : null
}

export function pageUrl(page: WebMcpServiceKey, lang: string): string {
  const l: Locale = getLocale(lang)
  const svc = webmcpServices.find((s) => s.key === page)
  const path = svc ? svc.path : 'pricing'
  return `${siteUrl}${localizedPath(l, path)}`
}

export function contactInfo(lang: string): { contactUrl: string; consoleUrl: string; consoleLogin: string } {
  const l: Locale = getLocale(lang)
  return {
    contactUrl: `${siteUrl}${localizedPath(l, 'contact')}`,
    consoleUrl: CONSOLE_URL,
    consoleLogin: consoleLoginUrl,
  }
}
